"use client";

import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { defaultTo, get } from "lodash-es";
import { useEffect, useMemo, useRef } from "react";
import { useLocale, useTranslations } from "use-intl";

import { AlbumGrid } from "@/components/album-grid";
import { InventoryIcon } from "@/components/appIcons";
import { DiscographyEmptyState } from "@/components/discography-empty-state";
import { DiscographyTypeHeader } from "@/components/discography-type-header";
import { FacetFilterBar } from "@/components/facets/facet-filter-bar";
import { handoffLinkTarget } from "@/components/handoff-link-target";
import type { LinkTarget } from "@/components/link";
import { COMPANY_DISCOGRAPHY_SURFACE } from "@/facets/config";
import type { FacetFiltersController } from "@/facets/use-facet-filters";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useListingCounts } from "@/hooks/use-listing-counts";
import { useReleaseGroupFilterOptions } from "@/hooks/use-release-group-filter-options";
import { useTopLoaderOnRefetch } from "@/hooks/use-top-loader-on-refetch";
import {
  GROUPED_DISCOGRAPHY_WORK_HANDOFF_EXCLUDED_KEYS,
  getWorkReleaseHandoffSearch,
} from "@/lib/faceted-search-url";
import {
  ALBUM_TYPE_OPTIONS,
  type FormatFilter,
  SORT_OPTIONS,
  type TypeFilter,
} from "@/routes/_app/artist/$id/artist-discography";
import { resolveDiscographyViewState } from "@/routes/_app/artist/$id/discography-view-state";
import type { DiscographyQueryType } from "@/types/discography";
import { type AlbumType } from "@garimpa/lib/albumTypes";
import { type DiscographySort as SortOption } from "@garimpa/lib/discographySorts";
import type { DiscographyTypeCounts } from "@garimpa/lib/resolveDiscographyDefaultType";

import { getCompanyAlbumsInfiniteQueryOptions } from "./company-albums-query";

export type { SortOption };
export { SORT_OPTIONS };
export const DEFAULT_SORT: SortOption = "popular";

const EMPTY_TYPE_COUNTS: Record<AlbumType | "all", number> = {
  all: 0,
  album: 0,
  compilation: 0,
  single_ep: 0,
  live: 0,
  video: 0,
  other: 0,
} as const;

interface CompanyDiscographyProps {
  companyId: string;
  companyName: string;
  discographyQueryType: DiscographyQueryType;
  discographyTypeCounts?: DiscographyTypeCounts | null;
  format: FormatFilter;
  sort: SortOption;
  facetControls: FacetFiltersController;
  searchValue: string;
  onSearchChange: (value: string) => void;
  updateFilter: (key: string, value: string | undefined) => void;
  getTypeLinkTarget?: (value: TypeFilter) => LinkTarget;
  getFormatLinkTarget?: (value: string) => LinkTarget;
}

export function CompanyDiscography({
  companyId,
  companyName,
  discographyQueryType,
  discographyTypeCounts,
  format,
  sort,
  facetControls,
  searchValue,
  onSearchChange,
  updateFilter,
  getTypeLinkTarget,
  getFormatLinkTarget,
}: CompanyDiscographyProps) {
  const t = useTranslations("label");
  const locale = useLocale();
  const sentinelRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useDebouncedValue(searchValue, 300);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isFetching } =
    useInfiniteQuery({
      ...getCompanyAlbumsInfiniteQueryOptions({
        companyId,
        discographyQueryType,
        filter: facetControls.filter,
        format,
        sort,
        search: debouncedSearch,
      }),
      placeholderData: keepPreviousData,
      select: (data) => ({
        items: data.pages.flatMap((page) => page.items),
        effectiveType: (data.pages[0]?.effectiveType ?? "all") as TypeFilter,
        facets: data.pages[0]?.facets ?? null,
      }),
    });
  useTopLoaderOnRefetch([isFetching, !isFetchingNextPage, !isLoading].every(Boolean));

  const effectiveType = get(data, "effectiveType") as TypeFilter | undefined;
  const albums = defaultTo(get(data, "items"), []);

  const typeCountsData = defaultTo(discographyTypeCounts, undefined);
  const typeCounts = defaultTo(typeCountsData, EMPTY_TYPE_COUNTS);
  const facetOptions = useReleaseGroupFilterOptions(get(data, "facets"), locale);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "0px 0px 400px 0px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const workIds = useMemo(() => albums.map((a: { id: string }) => a.id), [albums]);
  const { listingMap } = useListingCounts({ workIds });
  const { emptyReason, hasActiveFilters, isTypeResolved, selectedType } =
    resolveDiscographyViewState({
      discographyQueryType,
      effectiveType,
      typeCounts,
      typeCountsLoaded: typeCountsData !== undefined,
      albumCount: albums.length,
      isLoading,
      hasFacetFilters: facetControls.hasActive,
      searchValue,
    });

  if (emptyReason === "empty") {
    return (
      <section>
        <DiscographyEmptyState
          icon={InventoryIcon}
          title={t("noDiscographyTitle")}
          description={t("noDiscographyDescription")}
        />
      </section>
    );
  }

  const handleClearNarrowingFilters = () => {
    facetControls.clearAll();
    onSearchChange("");
  };

  return (
    <section>
      <DiscographyTypeHeader
        title={t("discography")}
        subtitle={typeCounts.all > 0 ? t("workCount", { count: typeCounts.all }) : undefined}
        allLabel={t("tabAll")}
        options={ALBUM_TYPE_OPTIONS.map((option) => ({
          key: option.key,
          label: t(option.labelKey),
          icon: option.icon,
        }))}
        counts={typeCounts}
        selectedType={selectedType}
        showTabs={isTypeResolved}
        onTypeChange={(type) => updateFilter("type", type)}
        getTypeLinkTarget={getTypeLinkTarget && ((type) => getTypeLinkTarget(type as TypeFilter))}
      />

      {[hasActiveFilters, defaultTo(get(data, "facets.totalCount"), 0) > 0].some(Boolean) && (
        <FacetFilterBar
          surface={COMPANY_DISCOGRAPHY_SURFACE}
          options={facetOptions}
          controller={facetControls}
          search={searchValue}
          onSearchChange={(value) => onSearchChange(value ?? "")}
          searchPlaceholder={t("searchPlaceholder")}
          formatLinkTarget={getFormatLinkTarget}
          layout="compact"
        />
      )}

      <AlbumGrid
        albums={albums}
        link={(album) =>
          handoffLinkTarget({
            to: "/work/$id",
            params: { id: album.id },
            search: getWorkReleaseHandoffSearch({
              excludeKeys: GROUPED_DISCOGRAPHY_WORK_HANDOFF_EXCLUDED_KEYS,
              filter: facetControls.filter,
              identityFilter: { OR: [{ label: companyName }, { company: companyName }] },
              q: searchValue,
            }),
          })
        }
        listingMap={listingMap}
        isLoading={isLoading}
        emptyState={
          emptyReason === "filtered" ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <p className="text-muted-foreground">{t("noAlbumsFilters")}</p>
              <button
                type="button"
                onClick={handleClearNarrowingFilters}
                className="text-sm font-medium text-primary hover:underline"
              >
                {t("clearFilters")}
              </button>
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-12">
              {searchValue ? t("noAlbumsSearch") : t("noAlbums")}
            </p>
          )
        }
      >
        {hasNextPage && (
          <div ref={sentinelRef} className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        )}
      </AlbumGrid>
    </section>
  );
}
